
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ArrowRight, Wallet, Calculator, Landmark, ClipboardList, LayoutDashboard, Warehouse, BrainCircuit, XCircle, CheckCircle2, Building2, Map, Wheat, Boxes, CircleDollarSign, ArrowRightLeft, Banknote, Beef, Wrench, Milk, FileBarChart2, CalendarCheck, FileText, TrendingUp, Quote, Award, Check, Star, MessageCircle } from 'lucide-react';

export const TopNav = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="fixed top-0 left-0 right-0 z-50 bg-gray-900/70 backdrop-blur-md border-b border-green-800/30"
    >
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-green-800 text-amber-400 font-bold">AG</span>
          <span className="text-white font-bold">AgroGestão</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/#oferta">
            <Button className="bg-amber-500 hover:bg-amber-600 text-green-950 font-bold">Experimente grátis</Button>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="bg-transparent text-white border-green-400 hover:bg-green-800/50 hover:text-white">Acessar</Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

const HeroSection = () => {
  return (
    <section 
      className="relative w-full min-h-screen flex items-center justify-center text-white bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url('https://img.freepik.com/free-photo/glowing-blue-hologram-with-plant-human-hand_188544-9679.jpg')` }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/80 backdrop-blur-md"></div>
      <div className="relative z-10 container mx-auto px-4 text-center space-y-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="inline-flex items-center gap-2 bg-green-800/50 text-green-200 px-3 py-1 rounded-full border border-green-700 text-center"
        >
          <ShieldCheck className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs sm:text-sm font-medium">Sistema para agricultura moderna | Multi-fazenda | Multi-dispositivo</span>
        </motion.div>

        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-shadow-lg"
        >
          A gestão financeira e produtiva que o produtor rural sempre mereceu.
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
          className="max-w-3xl mx-auto text-base sm:text-lg md:text-xl text-green-100/90 text-shadow"
        >
          Organize seus custos, controle suas safras, saiba seu lucro real e gere relatórios prontos para bancos — tudo em um único sistema fácil de usar.
        </motion.p>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
        >
          <Link to="/?action=signin">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-green-950 font-bold text-base w-full sm:w-auto group">
              Quero organizar minha fazenda agora
              <ArrowRight className="w-5 h-5 ml-2 transform transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
          <Link to="/Custos">
            <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white font-bold w-full sm:w-auto">
              Ver custos reais da minha safra
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// ... (rest of the sections will be implemented here)

const BenefitsSection = () => {
  const benefits = [
    {
      icon: <Wallet className="w-8 h-8 text-amber-400" />,
      title: "Controle financeiro sem complicação",
      description: "Visão clara de todas as suas receitas e despesas, fluxo de caixa e balanço patrimonial em tempo real.",
    },
    {
      icon: <Calculator className="w-8 h-8 text-amber-400" />,
      title: "Custo de produção automático",
      description: "Saiba exatamente o custo por hectare, por talhão e por safra, permitindo decisões mais estratégicas.",
    },
    {
      icon: <Landmark className="w-8 h-8 text-amber-400" />,
      title: "Relatórios para Bancos",
      description: "Gere relatórios profissionais e prontos para apresentar ao BB, Sicredi, Sicoob, e outros.",
    },
    {
      icon: <ClipboardList className="w-8 h-8 text-amber-400" />,
      title: "Consolidação Total",
      description: "Centralize dados de múltiplas fazendas e safras em um único dashboard consolidado.",
    },
    {
      icon: <LayoutDashboard className="w-8 h-8 text-amber-400" />,
      title: "Dashboard Inteligente",
      description: "Acompanhe os principais indicadores de desempenho (KPIs) da sua fazenda com gráficos interativos.",
    },
    {
      icon: <Warehouse className="w-8 h-8 text-amber-400" />,
      title: "Gestão de Estoques",
      description: "Controle o estoque de insumos e a produção armazenada nos seus armazéns e silos.",
    },
    {
        icon: <BrainCircuit className="w-8 h-8 text-amber-400" />,
        title: "Simulador de Cenários com IA",
        description: "Planeje a próxima safra com mais segurança, simulando diferentes cenários de custos e preços.",
      },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
      },
    },
  };

  return (
    <section id="benefits" className="py-20 sm:py-32 bg-gray-950">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white">Benefícios que transformam sua gestão</h2>
          <p className="mt-4 text-lg text-green-200/70 max-w-2xl mx-auto">
            O AgroGestão foi desenhado para resolver os desafios reais do produtor rural moderno.
          </p>
        </div>
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {benefits.map((benefit, index) => (
            <motion.div 
              key={index}
              className="bg-green-900/40 p-6 rounded-lg border border-green-800/50 shadow-lg hover:border-amber-400/50 transition-colors duration-300"
              variants={itemVariants}
            >
              <div className="flex items-center justify-center w-12 h-12 bg-green-800/50 rounded-full mb-4">
                {benefit.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
              <p className="text-green-200/80">{benefit.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
const WhySection = () => {
  const antes = [
    "Planilhas bagunçadas e dados espalhados",
    "Dificuldade para comprovar viabilidade ao banco",
    "Custos de produção calculados 'no chute'",
    "Confusão entre contas pessoais e da fazenda",
    "Falta de previsibilidade para a próxima safra",
  ];

  const depois = [
    "Tudo integrado em um único sistema",
    "Relatórios profissionais para o banco em 1 clique",
    "Controle total com custos calculados automaticamente",
    "Visão clara do que é da fazenda e do que é pessoal",
    "Tomada de decisão segura com previsões claras",
  ];

  const listVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    },
  };

  const itemVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: { 
      x: 0, 
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    },
  };

  return (
    <section id="why" className="py-20 sm:py-32">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white">Sua gestão está presa no passado?</h2>
          <p className="mt-4 text-lg text-green-200/70 max-w-3xl mx-auto">
            Veja a diferença entre continuar com métodos antigos e adotar uma gestão verdadeiramente profissional com o AgroGestão.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Coluna Antes */}
          <motion.div 
            className="bg-gray-900/50 p-8 rounded-lg border border-red-500/30"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <h3 className="text-2xl font-bold text-red-400 mb-6 text-center">O Jeito Antigo</h3>
            <motion.ul className="space-y-4" variants={listVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
              {antes.map((item, index) => (
                <motion.li key={index} className="flex items-start gap-3" variants={itemVariants}>
                  <XCircle className="w-6 h-6 text-red-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Coluna Depois */}
          <motion.div 
            className="bg-green-900/40 p-8 rounded-lg border-2 border-amber-400/80 shadow-2xl shadow-amber-500/10 flex flex-col"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          >
            <h3 className="text-2xl font-bold text-amber-400 mb-6 text-center">Com AgroGestão</h3>
            <motion.div 
              className="mb-8 shadow-lg rounded-lg"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.4, ease: 'easeOut' }}
            >
              <img 
                src="https://i.pinimg.com/originals/b2/5a/6e/b25a6e327a5c68a2d8755b16c0b5b157.png" 
                alt="Dashboard AgroGestão" 
                className="rounded-lg border-2 border-green-700/50"
              />
            </motion.div>
            <motion.ul className="space-y-4" variants={listVariants} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
              {depois.map((item, index) => (
                <motion.li key={index} className="flex items-start gap-3" variants={itemVariants}>
                  <CheckCircle2 className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                  <span className="text-white font-medium">{item}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
const ModulesSection = () => {
  const modules = [
    { icon: <Building2 />, title: "Propriedades", description: "Gerencie múltiplas fazendas com facilidade." },
    { icon: <Map />, title: "Talhões", description: "Controle áreas, culturas e produtividade." },
    { icon: <Wheat />, title: "Safras", description: "Resultado real, custo por hectare e margem de lucro." },
    { icon: <Boxes />, title: "Estoque de Insumos", description: "Entradas, saídas, notas fiscais e custo real de cada operação." },
    { icon: <Warehouse />, title: "Armazéns de Grãos", description: "Qualidade, umidade, impureza, contratos e logística." },
    { icon: <CircleDollarSign />, title: "Financeiro Completo", description: "Extratos, conciliações, fluxos, lançamentos e dashboards integrados." },
    { icon: <ArrowRightLeft />, title: "Contas a Pagar/Receber", description: "Organização total com baixas automáticas e auditoria." },
    { icon: <Banknote />, title: "Financiamentos", description: "Contratos, parcelas automáticas, pagamento e rastreabilidade." },
    { icon: <Beef />, title: "Animais/Rebanho", description: "Lotes, compra, venda, pesagens e integrações financeiras." },
    { icon: <Wrench />, title: "Ativos e Manutenção", description: "Máquinas, equipamentos, custos e desempenho." },
    { icon: <Milk />, title: "Produção de Leite", description: "Turnos, indicadores de qualidade e receita gerada." },
    { icon: <FileBarChart2 />, title: "Relatórios", description: "KPIs completos para decisão, auditorias e bancos." },
    { icon: <CalendarCheck />, title: "Orçamento Anual", description: "Planejamento comparando orçado vs realizado." },
    { icon: <BrainCircuit />, title: "Simulador Inteligente", description: "Lucros, custos, riscos e cenários de mercado." },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0, scale: 0.95 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: { type: 'spring', stiffness: 100, damping: 12 },
    },
  };

  return (
    <section id="modules" className="py-20 sm:py-32 bg-gray-950">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white">Uma plataforma, todas as soluções</h2>
          <p className="mt-4 text-lg text-green-200/70 max-w-2xl mx-auto">
            Explore os módulos que compõem o ecossistema completo do AgroGestão.
          </p>
        </div>
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {modules.map((module, index) => (
            <motion.div 
              key={index}
              className="bg-green-900/30 p-4 rounded-lg border border-green-800/40 text-center flex flex-col items-center justify-start hover:bg-green-800/50 hover:border-amber-400/50 transition-all duration-300 group"
              variants={itemVariants}
            >
              <div className="text-amber-400 mb-3 group-hover:scale-110 transition-transform duration-300">
                {React.cloneElement(module.icon, { className: "w-7 h-7" })}
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-1">{module.title}</h3>
              <p className="text-xs md:text-sm text-green-200/70">{module.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
const CreditSection = () => {
  const reports = [
    "DRE Rural (Demonstrativo de Resultados)",
    "Fluxo de Caixa Projetado e Realizado",
    "Histórico de Produção e Produtividade",
    "Custo Real por Hectare e por Saca",
    "Análise de Viabilidade Econômica e Financeira",
  ];

  return (
    <section id="credit" className="py-20 sm:py-32 bg-green-900/10">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white">Chega de sofrer para conseguir crédito</h2>
            <p className="mt-4 text-lg text-green-200/70">
              O AgroGestão gera automaticamente toda a documentação que o seu gerente precisa, no formato que o banco entende. Chega de planilhas e papéis soltos.
            </p>
            <motion.ul 
              className="mt-8 space-y-4"
              variants={{
                visible: { transition: { staggerChildren: 0.1 } },
              }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              {reports.map((report, index) => (
                <motion.li 
                  key={index} 
                  className="flex items-start gap-3"
                  variants={{ 
                    hidden: { opacity: 0, x: -20 },
                    visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } }
                  }}
                >
                  <FileText className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" />
                  <span className="text-white">{report}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
          <motion.div 
            className="bg-green-900/40 p-8 rounded-lg border border-amber-400/50 shadow-2xl shadow-amber-500/10 flex flex-col items-center text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div 
              className="mb-8 shadow-lg rounded-lg"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
            >
              <img 
                src="https://cdn.dribbble.com/userupload/3158904/file/original-3f531434385911f9525279ab2583244a.png?resize=1024x768" 
                alt="Exemplo de Relatório Financeiro do AgroGestão" 
                className="rounded-lg border-2 border-green-700/50"
              />
            </motion.div>
            <h3 className="text-2xl font-bold text-white">Aumente sua chance de aprovação</h3>
            <p className="text-green-200/80 mt-2 mb-6 max-w-sm mx-auto">Com relatórios profissionais e dados confiáveis, você reduz o risco percebido pelos bancos e negocia melhores condições.</p>
            <Link to="/Relatorios">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-green-950 font-bold w-full sm:w-auto group">
                  Gerar relatório para o banco
                  <ArrowRight className="w-5 h-5 ml-2 transform transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
const SocialProofSection = () => {
  const testimonials = [
    {
      quote: "Depois do AgroGestao, eu finalmente sei o lucro real da minha safra. Antes era tudo no escuro, agora eu tomo decisões com base em números.",
      name: "João Carlos Pereira",
      role: "Produtor de Soja, Mato Grosso",
      avatar: "https://img.freepik.com/free-photo/medium-shot-man-holding-tablet_23-2149456342.jpg",
    },
    {
      quote: "O relatório para o banco saiu em dois cliques. Meu gerente ficou impressionado com a organização. Facilitou demais a renovação do meu custeio.",
      name: "Maria Augusta Lima",
      role: "Cafeicultora, Sul de Minas",
      avatar: "https://img.freepik.com/free-photo/woman-holding-tablet-field_23-2148630951.jpg",
    },
    {
        quote: "Consigo gerenciar minhas três fazendas em um só lugar, até pelo celular. O controle de estoque de insumos me fez economizar muito na última compra.",
        name: "Ricardo Faria",
        role: "Pecuarista e Agricultor, Goiás",
        avatar: "https://img.freepik.com/free-photo/farmer-using-digital-tablet-psdd-agriculture-technology-icon_53876-124619.jpg",
      },
  ];

  const partners = ["Cooperativa Alfa", "AgroInvest", "Grupo Terra Forte", "Sicoob", "Sicredi"];

  return (
    <section id="social-proof" className="py-20 sm:py-32 bg-gray-950">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white">Quem usa, confia e recomenda</h2>
          <p className="mt-4 text-lg text-green-200/70 max-w-2xl mx-auto">
            Veja o que produtores rurais como você estão dizendo sobre o AgroGestão.
          </p>
        </div>

        {/* Testimonials */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
          variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {testimonials.map((testimonial, index) => (
            <motion.div 
              key={index}
              className="bg-green-900/30 p-6 rounded-lg border border-green-800/50 flex flex-col h-full"
              variants={{ 
                hidden: { opacity: 0, y: 50 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
              }}
            >
              <Quote className="w-8 h-8 text-amber-400/50 mb-4" />
              <p className="text-green-100/90 mb-6 flex-grow"><em>"{testimonial.quote}"</em></p>
              <div className="flex items-center gap-4 mt-auto">
                <img src={testimonial.avatar} alt={testimonial.name} className="w-14 h-14 rounded-full border-2 border-amber-400/60 object-cover"/>
                <div>
                  <p className="font-bold text-white">{testimonial.name}</p>
                  <p className="text-sm text-green-300/70">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Partners */}
        <motion.div 
          className="mt-24 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="text-lg font-semibold text-green-300 tracking-wider">Confiado por cooperativas e empresas que financiam o agro</h3>
          <motion.div 
            className="mt-8 flex flex-wrap justify-center items-center gap-x-8 gap-y-4"
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {partners.map((partner, index) => (
              <motion.span 
                key={index} 
                className="text-gray-500 font-medium text-lg hover:text-white transition-colors"
                variants={{ 
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
                }}
              >
                {partner}
              </motion.span>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
const OfferSection = () => {
  return (
    <section id="oferta" className="py-20 sm:py-32 bg-green-900/10">
      <div className="container mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white">Solicite um orçamento ou crie acesso gratuito</h2>
          <p className="mt-4 text-lg text-green-200/70 max-w-2xl mx-auto">Experimente por 15 dias sem compromisso ou receba uma proposta personalizada.</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contato">
              <Button size="lg" className="bg-green-700 hover:bg-green-800 text-white w-full sm:w-auto">Solicitar Orçamento</Button>
            </Link>
            <Link to="/login?action=signup">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-green-950 font-bold w-full sm:w-auto group">
                Criar acesso gratuito de 15 dias
                <ArrowRight className="w-5 h-5 ml-2 transform transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
export const Footer = () => {
  const links = [
    { name: "Home", path: "/" },
    { name: "Sobre", path: "/sobre" },
    { name: "Módulos", path: "/modulos" },
    { name: "Segurança", path: "/seguranca" },
    { name: "Privacidade", path: "/privacidade" },
    { name: "Contato", path: "/contato" },
  ];

  return (
    <footer id="footer" className="bg-gray-950 border-t border-green-800/30">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="mb-4 md:mb-0">
            <h3 className="text-2xl font-bold text-white">AgroGestão</h3>
            <p className="text-sm text-gray-400">Transformando a gestão do agronegócio.</p>
          </div>
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {links.map((link) => (
              <Link key={link.name} to={link.path} className="text-gray-300 hover:text-amber-400 transition-colors">
                {link.name}
              </Link>
            ))}
          </nav>
        </div>
        <div className="mt-8 border-t border-green-800/30 pt-6 text-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} AgroGestão. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

export default function LandingPage() {
  return (
    <div className="bg-gray-950 text-gray-100">
      <TopNav />
      <HeroSection />
      <BenefitsSection />
      <WhySection />
      <ModulesSection />
      <CreditSection />
      <SocialProofSection />
      <OfferSection />
      <FloatingWhatsApp />
      <Footer />
    </div>
  );
}

const FloatingWhatsApp = () => {
  const phone = '5564992648848';
  const message = encodeURIComponent('Olá! Gostaria de uma apresentação do AgroGestão, um orçamento e tirar algumas dúvidas.');
  const href = `https://wa.me/${phone}?text=${message}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Fale via WhatsApp"
      className="fixed bottom-6 right-6 z-50"
    >
      <div className="flex items-center gap-3 px-4 py-3 rounded-full shadow-lg border border-green-800/50 bg-green-700 hover:bg-green-800 text-white transition-colors">
        <MessageCircle className="w-5 h-5" />
        <span className="font-semibold">Fale via WhatsApp</span>
      </div>
    </a>
  );
};
